import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import httpService from "../services/httpService";

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';

import {
    FaSearch, FaTimes, FaHeadphones, FaCompass, FaMicrophone,
} from "react-icons/fa";

import ListenerPortfolioPodcastClub from "../components/ListenerComponents/ListenerPortfolioPodcastClub";
import PageHero from "../components/PageHero";
import "../styles/dashbaords.scss";
import "../styles/ListenerDashboard.scss";


const ListenerDashboard = () => {
    const [loading, setLoading]               = useState(true);
    const [error, setError]                   = useState(null);
    const [subscribedClubs, setSubscribedClubs] = useState([]);

    const [podcasterClubs, setPodcasterClubs]     = useState([]);
    const [podcasterLoading, setPodcasterLoading] = useState(true);
    const [titleReady, setTitleReady]             = useState(false);

    const [search, setSearch]         = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize]     = useState(10);
    const [sort, setSort]             = useState('subscribed_date');
    const [pagination, setPagination] = useState({
        total: 0, totalPages: 1, hasNextPage: false, hasPreviousPage: false,
    });

    useEffect(() => {
        fetchClubs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, pageSize, sort]);

    useEffect(() => {
        httpService.get("/dash/club/")
            .then((res) => setPodcasterClubs(res.data.data || []))
            .catch(() => {})
            .finally(() => setPodcasterLoading(false));
    }, []);

    useEffect(() => {
        if (!loading && !podcasterLoading) {
            setTitleReady(true);
        }
    }, [loading, podcasterLoading]);

    const fetchClubs = (page = pageNumber, searchValue = search) => {
        setLoading(true);
        const params = { pageNumber: page, pageSize, sort, ...(searchValue && { search: searchValue }) };
        httpService.get("/dash/get-subscribed-clubs", { params })
            .then((res) => {
                setSubscribedClubs(res.data.data || []);
                setPagination({
                    total:          res.data.total          || 0,
                    totalPages:     res.data.totalPages     || 1,
                    hasNextPage:    res.data.hasNextPage    || false,
                    hasPreviousPage: pageNumber > 1,
                });
            })
            .catch((err) => setError(err.response?.data?.message || "Error loading data"))
            .finally(() => setLoading(false));
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPageNumber(1);
        fetchClubs(1);
    };

    const handleClearSearch = () => {
        setSearch('');
        setPageNumber(1);
        fetchClubs(1, '');
    };

    return (
        <div id="ListenerDashboard-component">

            {/* ── Hero ── */}
            <PageHero>
                <div className="d-flex align-items-end justify-content-between flex-wrap gap-3">
                    <div>
                        <h1 className="page-hero__title">
                            <FaHeadphones className="me-2" style={{ opacity: 0.75 }} />
                            {titleReady
                                ? (podcasterClubs.length > 0 ? "My Clubs" : "Clubs You Subscribed To")
                                : null}
                        </h1>
                        {pagination.total > 0 && (
                            <p className="page-hero__sub">
                                {pagination.total} club{pagination.total !== 1 ? 's' : ''} subscribed
                            </p>
                        )}
                    </div>
                    <NavLink to="/listener-clubs" className="btn btn-outline-light btn-sm">
                        <FaCompass className="me-1" /> Discover more
                    </NavLink>
                </div>
            </PageHero>

            {/* ── Content ── */}
            <div className="container-lg py-4 px-3 px-lg-4">

                {/* Toolbar */}
                <Row className="mb-4 align-items-center g-2">
                    <Col md={5}>
                        <Form onSubmit={handleSearchSubmit}>
                            <div className="d-flex align-items-center gap-2">
                                <InputGroup size="sm">
                                    <Form.Control
                                        type="text"
                                        placeholder="Search my clubs…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    <Button variant="primary" type="submit"><FaSearch /></Button>
                                </InputGroup>
                                {search && (
                                    <Button variant="outline-secondary" size="sm" onClick={handleClearSearch}>
                                        <FaTimes />
                                    </Button>
                                )}
                            </div>
                        </Form>
                    </Col>
                    <Col md={4} className="d-flex align-items-center gap-2">
                        <span className="text-muted small text-nowrap">Sort:</span>
                        <Form.Select
                            size="sm"
                            value={sort}
                            onChange={(e) => { setSort(e.target.value); setPageNumber(1); }}
                            style={{ width: 'auto' }}
                        >
                            <option value="subscribed_date">Subscribed Date</option>
                            <option value="last_comment">Last Comment</option>
                            <option value="last_podcaster_activity">Last Podcaster Activity</option>
                        </Form.Select>
                    </Col>
                    <Col md={3} className="d-flex align-items-center justify-content-end gap-2">
                        <span className="text-muted small text-nowrap">Per page:</span>
                        <Form.Select
                            size="sm"
                            value={pageSize}
                            onChange={(e) => { setPageSize(parseInt(e.target.value)); setPageNumber(1); }}
                            style={{ width: 'auto' }}
                        >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="25">25</option>
                        </Form.Select>
                    </Col>
                </Row>

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>
                )}

                {/* Club list */}
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading…</span>
                        </Spinner>
                    </div>
                ) : subscribedClubs.length > 0 ? (
                    <>
                        {subscribedClubs.map((club) => (
                            <ListenerPortfolioPodcastClub key={club.id} club={club} />
                        ))}

                        {/* Pagination */}
                        <div className="ld-pagination">
                            <span>
                                {pagination.total} club{pagination.total !== 1 ? 's' : ''} total
                            </span>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    variant="outline-primary" size="sm"
                                    onClick={() => setPageNumber(p => p - 1)}
                                    disabled={pageNumber <= 1}
                                >
                                    Previous
                                </Button>
                                <span>Page {pageNumber} of {pagination.totalPages}</span>
                                <Button
                                    variant="outline-primary" size="sm"
                                    onClick={() => setPageNumber(p => p + 1)}
                                    disabled={!pagination.hasNextPage}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="ld-empty">
                        <FaHeadphones className="ld-empty__icon" />
                        <div className="ld-empty__title">You haven't subscribed to any clubs yet</div>
                        <p className="ld-empty__sub text-muted">
                            You haven't subscribed to any clubs.{' '}
                            <NavLink to="/listener-clubs">Discover clubs to join →</NavLink>
                        </p>
                    </div>
                )}

                {!podcasterLoading && podcasterClubs.length > 0 && (
                    <>
                        <hr className="my-4" />
                        <h5 className="mb-3">
                            <FaMicrophone className="me-2" style={{ opacity: 0.75 }} />
                            My Podcast Clubs
                        </h5>
                        {podcasterClubs.map((club) => (
                            <ListenerPortfolioPodcastClub
                                key={club.guid}
                                club={club}
                                linkTo="/podcaster/podcast-club/"
                            />
                        ))}
                    </>
                )}

            </div>
        </div>
    );
};

export default ListenerDashboard;
